import React, { useState, useMemo } from 'react';
import { LogIn, Mail, Eye, EyeOff, Check, X, ShieldCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';

const PW_RULES = [
  { label: 'At least 8 characters', test: (p) => p.length >= 8 },
  { label: 'One uppercase letter', test: (p) => /[A-Z]/.test(p) },
  { label: 'One lowercase letter', test: (p) => /[a-z]/.test(p) },
  { label: 'One number', test: (p) => /\d/.test(p) },
  { label: 'One special character (!@#$%...)', test: (p) => /[!@#$%^&*()_+\-=[\]{}|;:,.<>?]/.test(p) }
];

export default function LoginPage() {
  const navigate = useNavigate();
  const [isSignup, setIsSignup] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '', password: '', confirmPassword: '', name: '', termsAccepted: false
  });

  const pwStrength = useMemo(() => {
    if (!isSignup) return { score: 0, passed: [] };
    const passed = PW_RULES.filter(r => r.test(formData.password));
    return { score: passed.length, passed };
  }, [formData.password, isSignup]);

  const strengthColor = pwStrength.score <= 2 ? 'bg-red-500' : pwStrength.score <= 3 ? 'bg-yellow-500' : pwStrength.score <= 4 ? 'bg-blue-500' : 'bg-green-500';
  const passwordsMatch = formData.password && formData.confirmPassword && formData.password === formData.confirmPassword;

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin;
    const authUrl = process.env.REACT_APP_AUTH_URL || 'https://auth.emergentagent.com';
    window.location.href = `${authUrl}/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleEmailAuth = async (e) => {
    e.preventDefault();
    if (!formData.email || !formData.password) { toast.error('Please fill in all fields'); return; }
    if (isSignup && !formData.name) { toast.error('Please enter your name'); return; }
    if (isSignup && pwStrength.score < 5) { toast.error('Please meet all password requirements'); return; }
    if (isSignup && formData.password !== formData.confirmPassword) { toast.error('Passwords do not match'); return; }
    if (isSignup && !formData.termsAccepted) { toast.error('Please accept the Terms & Conditions'); return; }

    setLoading(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
      let response;
      if (isSignup) {
        response = await axios.post(`${API_URL}/auth/email/signup`, {
          email: formData.email, password: formData.password,
          confirm_password: formData.confirmPassword, name: formData.name,
          terms_accepted: formData.termsAccepted
        });
        toast.success('Account created successfully!');
      } else {
        response = await axios.post(`${API_URL}/auth/email/login`, {
          email: formData.email, password: formData.password
        });
        toast.success('Logged in successfully!');
      }

      const { user, session_token } = response.data;
      if (session_token) localStorage.setItem('session_token', session_token);
      localStorage.setItem('user', JSON.stringify(user));

      const pending = localStorage.getItem('pending_doctor_registration');
      if (pending) { navigate('/doctor/register'); return; }

      if (user.role === 'super_admin' || user.role === 'admin' || user.role === 'custom') {
        navigate('/admin/dashboard');
      } else if (user.role === 'doctor') {
        navigate('/doctor/dashboard');
      } else {
        navigate('/');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center px-6">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-2xl border border-slate-100 shadow-lg p-8">
          <div className="flex items-center justify-center mb-8">
            <img src="/afyagram_logo.png" alt="Afyagram" className="h-14 w-auto" />
          </div>

          <h1 className="font-manrope font-bold text-3xl text-[#0F2A44] text-center mb-2" data-testid="auth-title">
            {isSignup ? 'Create Account' : 'Welcome Back'}
          </h1>
          <p className="font-public text-[#4B5563] text-center mb-8">
            {isSignup ? 'Sign up to access Afyagram' : 'Sign in to your healthcare dashboard'}
          </p>

          <form onSubmit={handleEmailAuth} className="space-y-4 mb-6">
            {isSignup && (
              <div>
                <label className="block text-sm font-medium text-[#262626] mb-2">Full Name</label>
                <Input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Enter your name" data-testid="signup-name-input" className="h-12" required={isSignup} />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-[#262626] mb-2">Email</label>
              <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="Enter your email" data-testid="email-input" className="h-12" required />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#262626] mb-2">Password</label>
              <div className="relative">
                <Input type={showPassword ? 'text' : 'password'} value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} placeholder="Enter your password" data-testid="password-input" className="h-12 pr-10" required />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {/* Password Strength (signup only) */}
            {isSignup && formData.password && (
              <div className="space-y-2" data-testid="password-strength">
                <div className="flex gap-1">
                  {[1,2,3,4,5].map(i => (
                    <div key={i} className={`h-1.5 flex-1 rounded-full ${i <= pwStrength.score ? strengthColor : 'bg-slate-200'}`} />
                  ))}
                </div>
                <div className="space-y-1">
                  {PW_RULES.map((rule, i) => {
                    const passed = rule.test(formData.password);
                    return (
                      <div key={i} className={`flex items-center gap-2 text-xs ${passed ? 'text-green-600' : 'text-slate-400'}`}>
                        {passed ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
                        {rule.label}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Confirm Password (signup only) */}
            {isSignup && (
              <div>
                <label className="block text-sm font-medium text-[#262626] mb-2">Confirm Password</label>
                <div className="relative">
                  <Input type={showPassword ? 'text' : 'password'} value={formData.confirmPassword} onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })} placeholder="Confirm your password" data-testid="confirm-password-input" className="h-12 pr-10" required />
                  {formData.confirmPassword && (
                    <span className="absolute right-3 top-1/2 -translate-y-1/2">
                      {passwordsMatch ? <Check className="h-4 w-4 text-green-500" /> : <X className="h-4 w-4 text-red-500" />}
                    </span>
                  )}
                </div>
                {formData.confirmPassword && !passwordsMatch && (
                  <p className="text-xs text-red-500 mt-1">Passwords do not match</p>
                )}
              </div>
            )}

            {/* Terms & Conditions (signup only) */}
            {isSignup && (
              <label className="flex items-start gap-3 cursor-pointer" data-testid="terms-agreement">
                <input type="checkbox" checked={formData.termsAccepted} onChange={(e) => setFormData({ ...formData, termsAccepted: e.target.checked })} className="mt-1 h-4 w-4 rounded border-slate-300 text-[#2DD4BF]" data-testid="terms-checkbox-signup" />
                <span className="text-xs text-[#4B5563]">
                  I agree to the <Link to="/terms" target="_blank" className="text-[#4A90E2] hover:underline">Terms & Conditions</Link> and understand that Afyagram does not provide medical advice.
                </span>
              </label>
            )}

            <Button type="submit" disabled={loading || (isSignup && (!formData.termsAccepted || pwStrength.score < 5 || !passwordsMatch))} data-testid="email-auth-btn" className="w-full h-12 bg-[#0F2A44] hover:bg-[#0F2A44]/90 text-white rounded-lg font-manrope font-semibold disabled:opacity-50">
              {loading ? 'Please wait...' : <><Mail className="h-5 w-5 mr-2" />{isSignup ? 'Sign Up' : 'Sign In'}</>}
            </Button>
          </form>

          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200"></div></div>
            <div className="relative flex justify-center text-sm"><span className="px-4 bg-white text-[#4B5563]">OR</span></div>
          </div>

          <Button data-testid="google-login-btn" onClick={handleGoogleLogin} className="w-full h-12 bg-white hover:bg-slate-50 text-[#262626] border border-slate-200 rounded-lg font-manrope font-semibold flex items-center justify-center gap-3">
            <svg className="h-5 w-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Sign in with Google
          </Button>

          <div className="mt-6 text-center">
            <button type="button" onClick={() => { setIsSignup(!isSignup); setFormData({ ...formData, confirmPassword: '', termsAccepted: false }); }} className="font-public text-sm text-[#4A90E2] hover:underline" data-testid="toggle-auth-mode">
              {isSignup ? 'Already have an account? Sign in' : "Don't have an account? Sign up"}
            </button>
          </div>

          <p className="text-xs text-[#4B5563] text-center mt-6 font-public">
            By signing in, you agree to our <Link to="/terms" className="text-[#4A90E2] hover:underline">Terms of Service</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
